import React from "react";

import CircleIcon from "@mui/icons-material/Circle";
import isFunction from "lodash/isFunction";

import { FeedStatus } from "@fartherfinance/frontend/api/Notifications/Types";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import MenuContext from "@src/sharedComponents/Menu/MenuContext";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";

import { formatRelativeTimestamp } from "./Notification.util";

import styles from "./Notification.module.css";

type NotificationOwnProps = {
  message: string;
  topic: string | null;
  isUnseen: boolean;
  isSelected: boolean;
  createdAt: Date;
  onClick?: () => void;
  onStatusToggle: (toggledStatus: FeedStatus) => void;
};

type NotificationLoadingFalseProps = NotificationOwnProps & {
  isLoading: false;
};

type NotificationLoadingTrueProps = Never<NotificationOwnProps> & {
  isLoading: true;
};

type NotificationProps =
  | NotificationLoadingFalseProps
  | NotificationLoadingTrueProps;

const Notification: React.FC<NotificationProps> = ({
  message,
  topic,
  isUnseen,
  isSelected,
  isLoading,
  createdAt,
  onClick,
  onStatusToggle,
}) => {
  const isUnseenMarkVisible = isUnseen && !isLoading;
  const toggledStatus = isUnseen
    ? FeedStatus.Values.READ
    : FeedStatus.Values.UNREAD;

  const handleOnContainerClick = (): void => {
    if (isFunction(onClick) && !isLoading) {
      onClick();
    }
  };

  return (
    <MenuContext
      options={[
        {
          label: `Mark as ${isUnseen ? "read" : "unread"}`,
          isCloseOnClick: true,
          onClick: () => onStatusToggle?.(toggledStatus),
        },
      ]}
    >
      {({ handleContextMenu }) => (
        <div
          className={toClassName(styles.container, {
            [styles.selected]: isSelected,
            [styles.noHover]: isLoading,
          })}
          onClick={handleOnContainerClick}
          onContextMenu={handleContextMenu}
        >
          <div className={styles.content}>
            <div className={styles.header}>
              {isUnseenMarkVisible && (
                <CircleIcon
                  className={toClassName(styles.unseenMark, {
                    [styles.unseenMarkSelected]: isSelected,
                  })}
                />
              )}
              {isLoading ? (
                <Skeleton variant="rounded" width="100%" height="20px" />
              ) : (
                <Text
                  variant="body"
                  color={isSelected ? "textInverse" : "text"}
                  noWrap
                >
                  {message}
                </Text>
              )}
            </div>
            <div className={styles.footer}>
              {isLoading ? (
                <Skeleton variant="rounded" width="70%" height="16px" />
              ) : (
                <Text
                  variant="bodySmall"
                  color={isSelected ? "textInverse" : "textSoft"}
                  noWrap
                >
                  {topic}
                </Text>
              )}

              {isLoading ? (
                <Skeleton variant="rounded" width="10%" height="16px" />
              ) : (
                <Text
                  variant="bodySmall"
                  color={isSelected ? "textInverse" : "textSoft"}
                >
                  {formatRelativeTimestamp(createdAt)}
                </Text>
              )}
            </div>
          </div>
        </div>
      )}
    </MenuContext>
  );
};

export default Notification;
